import React from 'react';
import { X, FileText } from 'lucide-react';
import { theme } from '../styles/theme';

interface FilePreviewDialogProps {
  open: boolean;
  filePreview: string[];
  onClose: () => void;
}

/**
 * Properly parse CSV line respecting quoted fields with commas
 * Handles: "Field with, comma", "Field with "" quote", Field without quotes
 */
function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];
    
    if (char === '"') {
      // Handle escaped quotes: ""
      if (inQuotes && nextChar === '"') {
        current += '"';
        i++; // Skip next quote
      } else {
        // Toggle quote mode
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      // End of field
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  
  // Add last field
  result.push(current.trim());
  
  return result;
}

export const FilePreviewDialog: React.FC<FilePreviewDialogProps> = ({
  open,
  filePreview,
  onClose,
}) => {
  if (!open || filePreview.length === 0) return null;

  // Parse CSV data properly
  const headers = parseCSVLine(filePreview[0]);
  const rows = filePreview.slice(1, 51).map(line => parseCSVLine(line));

  return (
    <div
      className="fixed inset-0 bg-text-primary/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-bg-base border border-border rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <FileText className="w-6 h-6 text-primary" />
            <div>
              <h2 className={`text-xl font-semibold ${theme.text.primary}`}>
                File Preview
              </h2>
              <p className={`text-sm ${theme.text.secondary}`}>
                {filePreview.length} total rows • Showing first 50 data rows
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-bg-surface rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-text-secondary" />
          </button>
        </div>

        {/* Table Content */}
        <div className="flex-1 overflow-auto p-6">
          <div className="overflow-x-auto border border-border rounded-lg">
            <table className="w-full text-xs" style={{ minWidth: 'max-content' }}>
              <thead className="bg-bg-subtle sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left text-text-tertiary font-medium border-r border-border whitespace-nowrap">
                    Row #
                  </th>
                  {headers.map((header, idx) => (
                    <th
                      key={idx}
                      className={`px-3 py-2 text-left ${theme.text.primary} font-medium border-r border-border last:border-r-0 whitespace-nowrap`}
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((cells, rowIndex) => (
                  <tr
                    key={rowIndex}
                    className="hover:bg-bg-subtle transition-colors border-b border-border last:border-b-0"
                  >
                    <td className="px-3 py-2 text-text-tertiary font-mono border-r border-border whitespace-nowrap">
                      {rowIndex + 1}
                    </td>
                    {headers.map((_, cellIndex) => {
                      const cell = cells[cellIndex] || '';
                      return (
                        <td
                          key={cellIndex}
                          className={`px-3 py-2 ${theme.text.primary} border-r border-border last:border-r-0 whitespace-nowrap`}
                        >
                          {cell || (
                            <span className="text-text-tertiary italic">empty</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div className="mt-4 flex items-center gap-2 text-xs text-text-tertiary">
            <span></span>
            <span>Scroll horizontally to see all columns • Close with X or click outside</span>
          </div>
        </div>
      </div>
    </div>
  );
};
